import type { AuthConfig, InitParam } from "@next-social-platform/chatbot_sso";

/**
 * Single source of truth for the OIDC client config + initial init-mode
 * decision. Lifted out of `AuthContext` so the SDK's `<AuthProvider>` can
 * receive it as a stable prop computed once at mount.
 *
 * The init decision is URL-based (current path) and *resolved once* before
 * the provider mounts. Re-running with different options mid-life would
 * re-init() the SDK and clobber the live session — the SDK provider
 * intentionally does NOT watch `initOptions` for changes.
 */

const SSO_ORIGIN =
  process.env.REACT_APP_SSO_ORIGIN ?? "https://sso.chatbot.mn";
const OIDC_CLIENT_ID =
  process.env.REACT_APP_OIDC_CLIENT_ID ?? "booking-admin";

/**
 * Build the OIDC config. `redirect_uri` is the *current page* (sans /signin
 * and /signup) so SSO returns the operator to where they were trying to go,
 * not always the dashboard. The hand-rolled AuthContext used the same
 * pattern; preserving it here keeps deep-link behaviour identical.
 */
export function buildOidcConfig(): AuthConfig {
  const currentPath = window.location.pathname;
  const defaultCallbackPath = "/";
  const callbackPath =
    currentPath !== "/" && currentPath !== "/signin" && currentPath !== "/signup"
      ? currentPath
      : defaultCallbackPath;
  const redirectUri = window.location.origin + callbackPath;

  // Capture deep-link query state BEFORE the SDK's OIDC redirect strips it.
  // The chatbot.mn → booking-admin handoff lands here as
  // `/?business_id=<uuid>` so the operator's intended business gets
  // pre-selected. The SDK's redirect_uri is `origin + path` (no query),
  // so any query string is lost during the OIDC authorize→callback dance.
  // We stash it under our own key (NOT the SDK's `post_login_redirect`,
  // which the SDK auto-clears in `handlePostLoginRedirect` BEFORE the
  // `login` event fires — leaving SPA consumers nothing to restore).
  //
  // AuthContext reads + clears this key once `authenticated` flips true,
  // navigates via react-router.
  //
  // CRITICAL: `buildOidcConfig` runs at module load on EVERY page load,
  // including the OIDC callback (`/?code=...&state=...`). We must strip
  // the OAuth callback params before deciding whether to capture —
  // otherwise the callback reload overwrites the `business_id` saved on
  // the FIRST landing with `?code=&state=`, and the handoff target is
  // lost. We stash only the meaningful (non-OAuth) params, so the
  // callback reload sees nothing to save and preserves the earlier
  // entry. The restored URL is then a clean `/?business_id=<uuid>`.
  const OAUTH_PARAMS = [
    "code",
    "state",
    "error",
    "error_description",
    "session_state",
    "iss",
    "sso_bridge",
    "sso_bridge_msg",
  ];
  const rawParams = new URLSearchParams(window.location.search);
  for (const key of OAUTH_PARAMS) rawParams.delete(key);
  const meaningfulSearch = rawParams.toString();
  const hash = window.location.hash;
  if (meaningfulSearch || hash) {
    try {
      sessionStorage.setItem(
        DEEP_LINK_STATE_KEY,
        JSON.stringify({
          path: currentPath,
          search: meaningfulSearch ? `?${meaningfulSearch}` : "",
          hash,
          savedAt: Date.now(),
        }),
      );
    } catch {
      // sessionStorage unavailable (privacy mode); fall through to
      // bare redirect_uri. Operator falls back to last-selected
      // business — same as pre-fix behavior, not a regression.
    }
  }

  return {
    client_id: OIDC_CLIENT_ID,
    redirect_uri: redirectUri,
    authorization_endpoint: `${SSO_ORIGIN}/api/oidc/authorize`,
    token_endpoint: `${SSO_ORIGIN}/api/oidc/token`,
    userinfo_endpoint: `${SSO_ORIGIN}/api/oidc/userinfo`,
    scope: "openid profile email tenant roles",
    response_type: "code",
  };
}

/**
 * sessionStorage key for the captured deep-link query/hash that survives
 * the OIDC redirect. Distinct from the SDK's `post_login_redirect` key
 * so the SDK's auto-handler can't clear ours.
 */
export const DEEP_LINK_STATE_KEY = "cb_booking_admin_deep_link";

/** Shape stored under DEEP_LINK_STATE_KEY. `savedAt` lets the consumer
 *  drop stale entries (5-minute TTL). */
export type DeepLinkState = {
  path: string;
  search: string;
  hash: string;
  savedAt: number;
};

/** TTL for the deep-link entry — same posture as the SDK's
 *  REDIRECT_TTL_MS (10 minutes), but tighter at 5 min since the OIDC
 *  round-trip should complete in seconds. */
const DEEP_LINK_TTL_MS = 5 * 60 * 1000;

/**
 * Read + clear the captured deep-link state. Returns null if no entry
 * exists, the entry is malformed, or it's older than the TTL.
 */
export function consumeDeepLinkState(): DeepLinkState | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(DEEP_LINK_STATE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    sessionStorage.removeItem(DEEP_LINK_STATE_KEY);
  } catch {
    // Best-effort cleanup; we still try to parse + return what we have.
  }
  try {
    const parsed = JSON.parse(raw) as DeepLinkState;
    if (typeof parsed?.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > DEEP_LINK_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Initial init-options. Admin is operator-only: every route requires an
 * authenticated session, so we default to `login-required`. The SDK
 * auto-detects `isLoginCallback()` and forces `'check-sso'` internally
 * (3.1.1+ safety net) — no consumer branch needed for the OIDC return leg.
 *
 * `silentSsoUrl` is included for completeness; ignored by `login-required`
 * (the brief flash is intentional for routes that can't render without auth).
 * If the admin app ever grows a public route, this single change to
 * `'check-sso'` for that path would light up silent SSO.
 */
export function selectInitOptions(): InitParam {
  return {
    type: "login-required",
    silentSsoUrl: "/silent-check-sso.html",
  };
}
